import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useT } from '../../context/TenantContext';
import { useNavigate } from 'react-router-dom';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useRealtime } from '../../hooks/useRealtime';
import SLABadge from '../../components/common/SLABadge';
import MIcon from '../../components/common/MIcon';
import { StatusChip } from '../../components/common/Chip';
import { Button } from '../../components/ui';
import NewTicketModal from './NewTicketModal';

const PRIO_ORDER   = { critical:0, high:1, medium:2, low:3 };
const STATUS_ORDER = { escalated:0, open:1, in_progress:2, scheduled:3, resolved:4, forwarded:4 };
const PRIO_C  = { critical:'#D93025', high:'#1558D6', medium:'#E37400', low:'#1E8E3E' };
const PRIO_BG = { critical:'#FFEBEE', high:'#E8F0FE', medium:'#FFF3E0', low:'#E8F5E9' };
const SLA_MIN  = { critical:15, high:60, medium:240, low:480 };

function slaElapsed(ticket) {
  const slaMin    = SLA_MIN[ticket.priority] || 240;
  const createdAt = ticket.created_at?.includes('Z') ? ticket.created_at : ticket.created_at + 'Z';
  const elapsed   = (Date.now() - new Date(createdAt)) / 60000;
  return { over: elapsed >= slaMin, pct: Math.min(200, (elapsed / slaMin) * 100) };
}


/* ── Fila de ticket ────────────────────────────────────────────── */
function TicketRow({ ticket, navigate, onTake, showTake }) {
  const p  = PRIO_C[ticket.priority];
  const [taking, setTaking] = useState(false);

  const handleTake = async (e) => {
    e.stopPropagation();
    setTaking(true);
    try { await onTake(ticket.id); } finally { setTaking(false); }
  };

  return (
    <tr onClick={() => navigate(`/backdesk/tickets/${ticket.id}`)} style={{cursor:'pointer'}}>
      <td style={{width:4,padding:0}}>
        <div style={{width:4,minHeight:44,borderRadius:'0 2px 2px 0',background:p}}/>
      </td>
      <td style={{padding:'8px 14px 8px 10px'}}>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <div style={{fontSize:13,fontWeight:600,color:'#111827',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:220}}>{ticket.title}</div>
          {ticket._eventToday && (
            <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 6px',borderRadius:20,
              background:'#E8F0FE',color:'#1558D6',fontSize:10,fontWeight:700,flexShrink:0}}>
              <MIcon name="calendar_today" size={10} color="#1558D6"/> Hoy
            </span>
          )}
        </div>
        <div style={{fontSize:11,color:'#9CA3AF',marginTop:1,display:'flex',gap:5,overflow:'hidden'}}>
          <span style={{fontFamily:'monospace',flexShrink:0}}>{ticket.ref}</span>
          <span>·</span>
          <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ticket.requester}{ticket.requester_email ? ` · ${ticket.requester_email}` : ''}</span>
        </div>
      </td>
      <td style={{padding:'0 16px 0 0'}}><StatusChip status={ticket.status} /></td>
      <td style={{padding:'0 16px 0 0'}}><SLABadge ticket={ticket} showTime={true}/></td>
      {showTake && (
        <td style={{padding:'0 8px 0 0',width:64}}>
          {!ticket.assignee_id && (
            <button onClick={handleTake} disabled={taking}
              style={{height:22,padding:'0 9px',borderRadius:5,fontSize:10,fontWeight:700,cursor:'pointer',
                border:'1px solid #0085CA',background:'#E6F4FB',color:'#0085CA',whiteSpace:'nowrap',
                opacity:taking ? 0.5 : 1,transition:'opacity .1s'}}>
              {taking ? '…' : 'Tomar'}
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/* ── Sección con tabla (sin card propio, vive dentro del card padre) ── */
function TicketSection({ title, tickets, navigate, emptyMsg, accent, onTake, showTake }) {
  const [sortBy,  setSortBy]  = useState('priority');
  const [sortDir, setSortDir] = useState(1);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d * -1);
    else { setSortBy(key); setSortDir(1); }
  };

  const sorted = useMemo(() => [...tickets].sort((a, b) => {
    let v = 0;
    if (sortBy === 'priority') v = (PRIO_ORDER[a.priority] ?? 9) - (PRIO_ORDER[b.priority] ?? 9);
    else if (sortBy === 'status')  v = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    else if (sortBy === 'sla')     v = slaElapsed(b).pct - slaElapsed(a).pct;
    else if (sortBy === 'level')   v = b.level - a.level;
    else v = new Date(b.updated_at) - new Date(a.updated_at);
    return v * sortDir;
  }), [tickets, sortBy, sortDir]);

  const thStyle = (key) => ({
    padding:'8px 16px 8px 0',
    fontSize:10, fontWeight:700, letterSpacing:.5, textTransform:'uppercase',
    color: sortBy === key ? accent : '#9CA3AF',
    borderBottom:'1px solid #F3F4F6', background:'#FAFAFA',
    cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
    transition:'color .1s',
  });
  const arrow = (key) => sortBy === key ? (sortDir === 1 ? ' ↑' : ' ↓') : '';

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,minWidth:0,
      background:'#fff',border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden'}}>
      {/* Cabecera de sección */}
      <div style={{padding:'12px 16px',borderBottom:'1px solid #F3F4F6',background:'#FAFAFA',
        display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:9,height:9,borderRadius:'50%',background:accent,flexShrink:0}}/>
        <span style={{fontSize:13,fontWeight:700,color:'#111827'}}>{title}</span>
        <span style={{fontSize:11,fontWeight:700,padding:'1px 8px',borderRadius:10,
          background:accent+'18',color:accent}}>{tickets.length}</span>
      </div>
      {/* Tabla */}
      {sorted.length === 0
        ? <div style={{padding:'28px 16px',textAlign:'center',color:'#9CA3AF',fontSize:13,flex:1}}>{emptyMsg}</div>
        : (
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr>
                <th style={{width:4,padding:0}}/>
                <th onClick={() => toggleSort('updated')} style={{...thStyle('updated'),padding:'8px 14px 8px 10px'}}>Ticket{arrow('updated')}</th>
                <th onClick={() => toggleSort('status')}  style={thStyle('status')}>Estado{arrow('status')}</th>
                <th onClick={() => toggleSort('sla')}     style={thStyle('sla')}>SLA{arrow('sla')}</th>
                {showTake && <th style={{width:64,padding:0,borderBottom:'1px solid #F3F4F6',background:'#FAFAFA'}}/>}
              </tr>
            </thead>
            <tbody>
              {sorted.map(tk => (
                <TicketRow key={tk.id} ticket={tk} navigate={navigate}
                  onTake={onTake} showTake={showTake}/>
              ))}
            </tbody>
          </table>
        )
      }
    </div>
  );
}

/* ── Dashboard principal ───────────────────────────────────────── */
export default function Dashboard() {
  const { t } = useT();
  const navigate   = useNavigate();
  const { agent }  = useAuth();
  const [showNewTicket, setShowNewTicket] = useState(false);

  const [myTickets,    setMyTickets]    = useState([]);
  const [otherTickets, setOtherTickets] = useState([]);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);

  // Filtros
  const [search,   setSearch]   = useState('');
  const [priFilter, setPriFilter] = useState('all');

  const ACTIVE = ['open', 'in_progress', 'escalated'];

  const load = useCallback(async () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const [allRes, statsRes, todayEvents] = await Promise.all([
      api.getTickets('?limit=200'),
      api.getStats(),
      api.getEvents(todayStr, todayStr + 'T23:59:59'),
    ]);
    // IDs de tickets con evento hoy
    const scheduledTodayIds = new Set(
      (todayEvents || []).filter(e => e.ticket_id).map(e => e.ticket_id)
    );
    const all = (allRes.data || []).filter(tk =>
      ACTIVE.includes(tk.status) ||
      (tk.status === 'scheduled' && scheduledTodayIds.has(tk.id))
    ).map(tk => ({ ...tk, _eventToday: scheduledTodayIds.has(tk.id) }));
    setMyTickets(all.filter(tk => tk.assignee_id === agent?.id));
    setOtherTickets(all.filter(tk => tk.assignee_id !== agent?.id));
    setStats(statsRes);
    setLoading(false);
  }, [agent?.id]);

  useEffect(() => { load(); }, [load]);

  useRealtime(useCallback((event) => {
    if (['ticket_new','ticket_updated','ticket_escalated'].includes(event.type)) load();
  }, [load]));

  const handleTake = useCallback(async (ticketId) => {
    await api.updateTicket(ticketId, { assignee_id: agent?.id });
    load();
  }, [agent?.id, load]);

  // Aplicar filtros
  const applyFilters = useCallback((list) => {
    let r = list;
    if (priFilter !== 'all') r = r.filter(tk => tk.priority === priFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(tk =>
        tk.title?.toLowerCase().includes(q) ||
        tk.ref?.toLowerCase().includes(q) ||
        tk.requester?.toLowerCase().includes(q) ||
        tk.requester_email?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [search, priFilter]);

  const filteredMine  = useMemo(() => applyFilters(myTickets),    [myTickets,    applyFilters]);
  const filteredOther = useMemo(() => applyFilters(otherTickets),  [otherTickets, applyFilters]);

  if (loading) return (
    <BackDeskLayout title="Dashboard">
      <div className="bd-loading"><div className="bd-spinner-sm"/>Cargando…</div>
    </BackDeskLayout>
  );

  const byStatus    = Object.fromEntries((stats?.byStatus   || []).map(r => [r.status,   r.count]));
  const criticalOpen = myTickets.filter(tk => tk.priority === 'critical').length
                     + otherTickets.filter(tk => tk.priority === 'critical').length;
  return (
    <BackDeskLayout>
      {showNewTicket && (
        <NewTicketModal
          onClose={() => setShowNewTicket(false)}
          onCreated={(t) => { setShowNewTicket(false); load(); if (t?.id) navigate(`/backdesk/tickets/${t.id}`); }}
        />
      )}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:16}}>
        {[
          {l:t('dashboard.my_tickets'), v:myTickets.length},
          {l:t('dashboard.unassigned'),         v:otherTickets.filter(tk=>!tk.assignee_id).length},
          {l:t('dashboard.escalated'),           v:byStatus.escalated || 0},
          {l:t('dashboard.critical_active'),    v:criticalOpen},
          {l:t('dashboard.resolved_today'),       v:stats?.resolvedToday || 0},
        ].map(s => (
          <div key={s.l} style={{background:'#fff',border:'none',borderRadius:12,
            padding:'16px 18px',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
            <div style={{fontSize:10,fontWeight:600,letterSpacing:.4,textTransform:'uppercase',
              color:'#9CA3AF',marginBottom:8}}>{s.l}</div>
            <div style={{fontSize:28,fontWeight:700,color:'var(--bd-accent)',lineHeight:1}}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Card principal: buscador + tablas */}
      <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,
        boxShadow:'0 1px 3px rgba(0,0,0,.05)',overflow:'hidden'}}>

        {/* Buscador + filtro prioridad + nuevo ticket */}
        <div style={{padding:'12px 16px',borderBottom:'1px solid #F3F4F6',
          display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{position:'relative',flex:'1 1 200px',maxWidth:300}}>
            <span style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',
              display:'flex',alignItems:'center',pointerEvents:'none'}}>
              <MIcon name="search" size={16} color="#9CA3AF"/>
            </span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('dashboard.search')}
              style={{width:'100%',height:32,paddingLeft:28,paddingRight:10,border:'1px solid #E5E7EB',
                borderRadius:8,fontSize:12,color:'#111827',background:'#F9FAFB',outline:'none',
                boxSizing:'border-box'}}
            />
          </div>
          <div style={{width:1,height:20,background:'#E5E7EB',flexShrink:0}}/>
          <div style={{display:'flex',gap:4,flexShrink:0,flexWrap:'wrap'}}>
            {[['all','Todas'],['critical','Crítica'],['high','Alta'],['medium','Media'],['low','Baja']].map(([k,l]) => (
              <button key={k} onClick={() => setPriFilter(k)}
                style={{height:28,padding:'0 11px',borderRadius:7,fontSize:11,fontWeight:600,
                  cursor:'pointer',border:'1px solid',transition:'all .12s',
                  borderColor: priFilter===k ? (PRIO_C[k]||'#0085CA') : '#E5E7EB',
                  background:  priFilter===k ? (PRIO_BG[k]||'#E6F4FB') : 'transparent',
                  color:       priFilter===k ? (PRIO_C[k]||'#0085CA')  : '#6B7280'}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{flex:1}}/>
          <Button variant="primary" onClick={() => setShowNewTicket(true)} style={{flexShrink:0}}>
            {t('dashboard.new_ticket')}
          </Button>
        </div>

        {/* Tablas lado a lado */}
        <div className="bd-dashboard-cols">
          <TicketSection
            title={t('dashboard.my_assigned')}
            tickets={filteredMine}
            navigate={navigate}
            emptyMsg="No tienes tickets asignados actualmente 🎉"
            accent="#0085CA"
            showTake={false}
          />
          <TicketSection
            title={t('dashboard.team_queue')}
            tickets={filteredOther}
            navigate={navigate}
            emptyMsg="No hay tickets pendientes en la cola"
            accent="#7C3AED"
            onTake={handleTake}
            showTake={true}
          />
        </div>
      </div>
    </BackDeskLayout>
  );
}
